/**
 * Helpers for useJournalEntries hook
 */

import { listJournalEntries } from '../../services/journal-entry-service'
import {
  getLocalJournalEntries,
  initializeLocalDb,
} from '../../services/local-db'

import type { DisplayEntry } from './journal-entries-helpers'
import type { FilterBarCondition } from '@elsapiens/ui'

// ── API filter builder ────────────────────────────────────────────────────────

function applyDateRangeFilter(
  f: Record<string, unknown>,
  c: FilterBarCondition,
): void {
  if (c.operator !== 'between' || !c.value) {return}
  const dateVal = c.value as { start?: string; end?: string }
  if (dateVal.start) { f.startDate = dateVal.start }
  if (dateVal.end) { f.endDate = dateVal.end }
}

function applyCondition(
  f: Record<string, unknown>,
  c: FilterBarCondition,
): void {
  if (c.fieldId === 'status' && c.value) { f.status = c.value; return }
  if (c.fieldId === 'entryType' && c.value) { f.entryType = c.value; return }
  if (c.fieldId === 'entryDate') { applyDateRangeFilter(f, c) }
}

export function buildApiFilters(
  searchQuery: string,
  conditions: FilterBarCondition[],
): Record<string, unknown> {
  const f: Record<string, unknown> = {}
  if (searchQuery) { f.search = searchQuery }
  for (const c of conditions) {
    applyCondition(f, c)
  }
  return f
}

// ── Sorting helper ────────────────────────────────────────────────────────────

function getSortValue(entry: DisplayEntry, sortField: string): unknown {
  if (sortField === 'totalDebit') { return Number(entry.totalDebit.amount) }
  if (sortField === 'totalCredit') { return Number(entry.totalCredit.amount) }
  return entry[sortField as keyof DisplayEntry]
}

function compareSortValues(
  aVal: unknown,
  bVal: unknown,
  sortDirection: 'asc' | 'desc',
): number {
  if (typeof aVal === 'string' && typeof bVal === 'string') {
    return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
  }
  if (typeof aVal === 'number' && typeof bVal === 'number') {
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
  }
  return 0
}

export function sortEntries(
  entries: DisplayEntry[],
  sortField: string,
  sortDirection: 'asc' | 'desc',
): DisplayEntry[] {
  const sorted = [...entries]
  sorted.sort((a, b) => compareSortValues(
    getSortValue(a, sortField),
    getSortValue(b, sortField),
    sortDirection,
  ))
  return sorted
}

// ── Local-db entry mapper ─────────────────────────────────────────────────────

export type LocalEntry = Awaited<ReturnType<typeof getLocalJournalEntries>>[number]

function mapLocalEntryWithTotals(
  e: LocalEntry,
  baseCurrency: string,
  entryStatus: string,
  totalDebit: NonNullable<LocalEntry['totalDebit']>,
  totalCredit: NonNullable<LocalEntry['totalCredit']>,
): DisplayEntry {
  const hasFx = totalDebit.currency !== baseCurrency
  return {
    id: e.id,
    entryNumber: e.entryNumber,
    entryDate: e.entryDate,
    entryType: e.entryType,
    memo: e.memo ?? null,
    status: entryStatus,
    totalDebit,
    totalCredit,
    totalDebitBase: e.totalDebitBase,
    totalCreditBase: e.totalCreditBase,
    _hasForex: hasFx,
    isLocal: true,
  }
}

function mapLocalEntryFromLines(e: LocalEntry, baseCurrency: string, entryStatus: string): DisplayEntry {
  let totalDebit = 0
  let totalCredit = 0
  const entryCurrency = e.lines[0]?.currency ?? baseCurrency
  for (const line of e.lines) {
    totalDebit += line.debitAmount ?? 0
    totalCredit += line.creditAmount ?? 0
  }
  return {
    id: e.id,
    entryNumber: e.entryNumber,
    entryDate: e.entryDate,
    entryType: e.entryType,
    memo: e.memo ?? null,
    status: entryStatus,
    totalDebit: { amount: totalDebit, currency: entryCurrency },
    totalCredit: { amount: totalCredit, currency: entryCurrency },
    totalDebitBase: { amount: totalDebit, currency: baseCurrency },
    totalCreditBase: { amount: totalCredit, currency: baseCurrency },
    isLocal: true,
  }
}

export function mapLocalEntry(e: LocalEntry, baseCurrency: string): DisplayEntry {
  const entryStatus = `ENTRY_STATUS_${e.status.toUpperCase()}`

  if (e.totalDebit && e.totalCredit) {
    return mapLocalEntryWithTotals(e, baseCurrency, entryStatus, e.totalDebit, e.totalCredit)
  }
  return mapLocalEntryFromLines(e, baseCurrency, entryStatus)
}

function matchesSearch(e: LocalEntry, search: string): boolean {
  const q = search.toLowerCase()
  const isMatchingMemo = e.memo?.toLowerCase().includes(q) ?? false
  const isMatchingRef = e.reference?.toLowerCase().includes(q) ?? false
  const isMatchingNum = e.entryNumber.toLowerCase().includes(q)
  return isMatchingMemo || isMatchingRef || isMatchingNum
}

function matchesStatusFilter(e: LocalEntry, status: unknown): boolean {
  const entryStatus = `ENTRY_STATUS_${e.status.toUpperCase()}`
  // No explicit status ("All") mirrors the backend: voided entries are
  // excluded by default, only shown via an explicit status filter.
  if (!status) {return entryStatus !== 'ENTRY_STATUS_VOIDED'}
  return entryStatus === status
}

function matchesDateRange(e: LocalEntry, apiFilters: Record<string, unknown>): boolean {
  if (apiFilters.startDate && e.entryDate < (apiFilters.startDate as string)) { return false }
  if (apiFilters.endDate && e.entryDate > (apiFilters.endDate as string)) { return false }
  return true
}

function entryMatchesFilters(
  e: LocalEntry,
  apiFilters: Record<string, unknown>,
): boolean {
  if (!matchesStatusFilter(e, apiFilters.status)) {return false}
  if (apiFilters.entryType && e.entryType !== apiFilters.entryType) {return false}
  if (!matchesDateRange(e, apiFilters)) {return false}
  if (apiFilters.search && !matchesSearch(e, apiFilters.search as string)) {return false}
  return true
}

export function filterLocalEntries(
  entries: LocalEntry[],
  apiFilters: Record<string, unknown>,
): LocalEntry[] {
  return entries.filter(e => entryMatchesFilters(e, apiFilters))
}

// ── Entry fetching ────────────────────────────────────────────────────────────

type ApiEntry = Awaited<ReturnType<typeof listJournalEntries>>['data'] extends { entries: (infer T)[] } | undefined ? T : never

function mapApiEntry(e: ApiEntry, baseCurrency: string): DisplayEntry {
  // e.totalDebit / e.totalCredit can come back null from the API (seen on
  // migrated/legacy entries) — default to a zero amount in the org's base
  // currency rather than crashing the whole page's entry list over one
  // malformed row.
  const totalDebit = e.totalDebit
    ? { ...e.totalDebit, amount: Number(e.totalDebit.amount) }
    : { amount: 0, currency: baseCurrency }
  const totalCredit = e.totalCredit
    ? { ...e.totalCredit, amount: Number(e.totalCredit.amount) }
    : { amount: 0, currency: baseCurrency }
  return {
    id: e.id,
    entryNumber: e.entryNumber,
    entryDate: e.entryDate,
    entryType: e.entryType,
    memo: e.memo ?? null,
    status: e.status,
    totalDebit,
    totalCredit,
    totalDebitBase: e.totalDebitBase
      ? { amount: Number(e.totalDebitBase.amount), currency: e.totalDebitBase.currency }
      : totalDebit,
    totalCreditBase: e.totalCreditBase
      ? { amount: Number(e.totalCreditBase.amount), currency: e.totalCreditBase.currency }
      : totalCredit,
    isLocal: false,
  }
}

export interface PageMeta {
  nextPageToken: string
  hasNextPage: boolean
  totalCount?: number
}

const EMPTY_PAGE_META: PageMeta = { nextPageToken: '', hasNextPage: false }

interface ApiFetchResult {
  entries: DisplayEntry[]
  pageInfo: PageMeta
}

async function fetchFromApi(
  organizationId: string,
  apiFilters: Record<string, unknown>,
  baseCurrency: string,
  pageSize?: number,
  pageToken?: string,
): Promise<ApiFetchResult | null> {
  try {
    const response = await listJournalEntries(organizationId, apiFilters, pageSize, pageToken)
    if (response.data && response.data.entries.length > 0) {
      const entries = response.data.entries.map(e => mapApiEntry(e, baseCurrency))
      const pi = response.data.pageInfo
      return {
        entries,
        pageInfo: pi
          ? { nextPageToken: pi.nextPageToken, hasNextPage: pi.hasNextPage, totalCount: pi.totalCount }
          : EMPTY_PAGE_META,
      }
    }
  } catch {
    // API failed
  }
  return null
}

async function fetchFromLocalDb(
  organizationId: string,
  apiFilters: Record<string, unknown>,
  baseCurrency: string,
): Promise<DisplayEntry[]> {
  try {
    await initializeLocalDb(organizationId)
    const localEntries = await getLocalJournalEntries(organizationId)
    const filtered = filterLocalEntries(localEntries, apiFilters)
    return filtered.map(e => mapLocalEntry(e, baseCurrency))
  } catch {
    return []
  }
}

export interface FetchEntriesResult {
  entries: DisplayEntry[]
  pageInfo: PageMeta
  error?: string
}

export async function fetchAllEntries(params: {
  organizationId: string
  apiFilters: Record<string, unknown>
  baseCurrency: string
  minLoadingTime: Promise<unknown>
  pageSize?: number
  pageToken?: string
}): Promise<FetchEntriesResult> {
  const { organizationId, apiFilters, baseCurrency, minLoadingTime, pageSize, pageToken } = params
  try {
    const apiResult = await fetchFromApi(organizationId, apiFilters, baseCurrency, pageSize, pageToken)
    if (apiResult) {
      await minLoadingTime
      return { entries: apiResult.entries, pageInfo: apiResult.pageInfo }
    }

    // Local-db fallback only runs on the first page — paging local data is out of scope.
    if (pageToken) {
      await minLoadingTime
      return { entries: [], pageInfo: EMPTY_PAGE_META }
    }

    const localEntries = await fetchFromLocalDb(organizationId, apiFilters, baseCurrency)
    await minLoadingTime
    return { entries: localEntries, pageInfo: EMPTY_PAGE_META }
  } catch {
    await minLoadingTime
    return { entries: [], pageInfo: EMPTY_PAGE_META, error: 'Failed to load entries. Please try again later.' }
  }
}

// ── Initial state helpers ─────────────────────────────────────────────────────

export function getInitialConditions(initialTab: string): FilterBarCondition[] {
  if (initialTab === 'drafts') {
    return [{ id: 'tab-status', fieldId: 'status', operator: 'equals' as const, value: 'ENTRY_STATUS_DRAFT' }]
  }
  if (initialTab === 'posted') {
    return [{ id: 'tab-status', fieldId: 'status', operator: 'equals' as const, value: 'ENTRY_STATUS_POSTED' }]
  }
  if (initialTab === 'voided') {
    return [{ id: 'tab-status', fieldId: 'status', operator: 'equals' as const, value: 'ENTRY_STATUS_VOIDED' }]
  }
  return []
}
